import { cn } from "@/utils/cnHelper";
import * as React from "react";

interface TextareaProps extends Omit<React.ComponentProps<"textarea">, "theme"> {
  theme?: "dark" | "light";
  radius?: "none" | "sm" | "md" | "lg" | "full";
  resize?: "none" | "both" | "horizontal" | "vertical";
}

function Textarea({ className, theme = "dark", radius = "md", resize = "vertical", rows = 4, ...props }: TextareaProps) {
  const themeClasses =
    theme === "dark"
      ? "tw-bg-grey_scale-900 tw-text-white tw-border-grey_scale-600 tw-placeholder-grey_scale-400"
      : "tw-bg-white tw-text-grey_scale-800 tw-border-grey_scale-200 tw-placeholder-grey_scale-500";

  const focusClasses =
    theme === "dark"
      ? "focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-shorthand_teal focus-visible:tw-ring-offset-2 focus-visible:tw-ring-offset-grey_scale-900"
      : "focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-brand_scale-500 focus-visible:tw-ring-offset-2 focus-visible:tw-ring-offset-white";

  const radiusClasses = {
    none: "tw-rounded-none",
    sm: "tw-rounded-sm",
    md: "tw-rounded-lg",
    lg: "tw-rounded-xl",
    full: "tw-rounded-2xl", // full radius on textarea should be more moderate
  }[radius];

  const resizeClasses = {
    none: "tw-resize-none",
    both: "tw-resize",
    horizontal: "tw-resize-x",
    vertical: "tw-resize-y",
  }[resize];

  return (
    <div className="tw-w-full">
      <textarea
        rows={rows}
        className={cn(
          "tw-w-full tw-border !tw-px-4 !tw-py-3 tw-align-bottom tw-text-base tw-placeholder-grey_scale-400",
          "tw-selection:bg-primary tw-selection:text-primary-foreground tw-shadow-xs tw-md:text-sm tw-disabled:pointer-events-none tw-disabled:cursor-not-allowed tw-disabled:opacity-50",
          themeClasses,
          focusClasses,
          radiusClasses,
          resizeClasses,
          className
        )}
        {...props}
      />
    </div>
  );
}

Textarea.displayName = "Textarea";

export { Textarea };
